import { FC, useContext, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import NotificationDispatch, { showErrorNotification } from "../../../../../context/notificationContext";
import { useSearchContext } from "../../../../../context/searchContext";
import { useDebounce } from "../../../../../hooks/useDebounce";
import { useEntityAccesses } from "../../../../../hooks/useEntityAccesses";
import { useFilterSearch } from "../../../../../hooks/useFilterSearch";
import useListFilters from "../../../../../hooks/useListFilters";
import useI18n from "../../../../../hooks/useTranslations";
import { AdminBorderSystem } from "../../../../../http/border/types/borderSystems";
import { useBorderSystemsApi } from "../../../../../http/border/useBorderSystems";
import { PaginationQuery } from "../../../../../types";
import { formatBoolean } from "../../../../../utils";
import Button from "../../../../ui/Button";
import FlexContent from "../../../../ui/FlexContent";
import InlineOrderEditor from "../../../../ui/InlineOrderEditor";
import { getDeleteAction, getEditAction } from "../../../../ui/ListItemMenu";
import ModalDialog from "../../../../ui/ModalDialog";
import SearchBar from "../../../../ui/SearchBar";
import Table from "../../../../ui/Table";
import Visibility from "../../../../ui/Visibility";
import { VisibilityScopeValue } from "../../../../ui/VisibilityScope";
import SystemForm from "./SystemForm";

type Props = {
  // Header actions (search + create) are only published to the shared Header while
  // this tab is the active one, since Tabs keeps every tab's content mounted at once.
  active?: boolean;
};

const BorderSystems: FC<Props> = ({ active }) => {
  const { systemId } = useParams<{ systemId: string }>();
  const i18n = useI18n();
  const navigate = useNavigate();
  const dispatch = useContext(NotificationDispatch);
  const filters = useListFilters({ key: "borderSystems" });
  const aborter = useRef(new AbortController());
  const systemsApi = useBorderSystemsApi();
  const { setActions, setTitle } = useSearchContext();
  const { searchTerm, handleSearchChange } = useFilterSearch(filters, aborter, "borderSystems");

  const isActiveTab = active ?? true;

  const updateVisibility = async (id: string, visibility: VisibilityScopeValue) => {
    try {
      await systemsApi.updateAdminBorderSystemVisibility(id, { visibility });
    } catch (err) {
      dispatch(showErrorNotification(i18n, err));
    }
  };

  const updateOrder = async (id: string, newOrder: number) => {
    try {
      await systemsApi.updateAdminBorderSystemOrder(id, { order: newOrder });
    } catch (err) {
      dispatch(showErrorNotification(i18n, err));
    }
  };

  const [showNewItemDialog, setShowNewItemDialog] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<AdminBorderSystem>();
  const { accessesAction, accessesDialog } = useEntityAccesses<AdminBorderSystem>("border.system");

  const debouncedValue = useDebounce(searchTerm);

  const query: PaginationQuery = {
    ...filters.state,
    q: debouncedValue,
  };

  const { error, data } = systemsApi.useAdminBorderSystems(query, aborter.current.signal);

  useEffect(() => {
    if (!isActiveTab) return;

    setTitle(i18n.translation.systems.plural);
    setActions(
      <>
        <SearchBar placeholder={i18n.translation.systems.search} value={searchTerm} onChange={handleSearchChange} />
        <Button buttonProps={{ onClick: () => setShowNewItemDialog(true) }}>{i18n.translation.common.createNew}</Button>
      </>,
    );

    return () => {
      setTitle(undefined);
      setActions(null);
    };
  }, [isActiveTab, searchTerm]);

  return (
    <FlexContent padding>
      <Table<AdminBorderSystem>
        isTall
        filters={filters}
        head={
          <tr>
            <th align="left">{i18n.translation.common.name}</th>
            <th align="left">{i18n.translation.common.isSubstructureRequired}</th>
            <th align="left">{i18n.translation.common.isVerticalBaseTypeRequired}</th>
            <th align="left">{i18n.translation.common.isBallastRequired}</th>
            <th align="left">{i18n.translation.common.isWaterproofKitVisible}</th>
            <th align="left">{i18n.translation.common.isWedgeVisible}</th>
            <th align="left">{i18n.translation.common.isFasteningVisible}</th>
            <th align="left">{i18n.translation.common.isSubstructureDetailsRequired}</th>
            <th>{i18n.translation.common.visibility}</th>
            <th>{i18n.translation.common.order}</th>
            <th></th>
          </tr>
        }
        items={data?.items}
        renderRow={(item) => (
          <>
            <td>
              <Link onClick={(e) => e.stopPropagation()} to={`/admin/systems/${systemId}/border_systems/${item.id}`}>
                {item.name}
              </Link>
            </td>
            <td>{formatBoolean(i18n.translation, item.isSubstructureRequired)}</td>
            <td>{formatBoolean(i18n.translation, item.isVerticalBaseTypeRequired)}</td>
            <td>{formatBoolean(i18n.translation, item.isBallastRequired)}</td>
            <td>{formatBoolean(i18n.translation, item.isWaterproofKitVisible)}</td>
            <td>{formatBoolean(i18n.translation, item.isWedgeVisible)}</td>
            <td>{formatBoolean(i18n.translation, item.isFasteningVisible)}</td>
            <td>{formatBoolean(i18n.translation, item.isSubstructureDetailsRequired)}</td>
            <td align="center">
              <Visibility value={item.visibility} onChange={(value) => updateVisibility(item.id, value)} />
            </td>
            <td align="center">
              <InlineOrderEditor order={item.order} onUpdate={(newOrder) => updateOrder(item.id, newOrder)} />
            </td>
          </>
        )}
        onRowClick={(item) => navigate(`/admin/systems/${systemId}/border_systems/${item.id}`)}
        rowActions={(item) => [
          getEditAction(i18n, () => navigate(`/admin/systems/${systemId}/border_systems/${item.id}`)),
          accessesAction(item),
          getDeleteAction(i18n, () => setItemToDelete(item), true),
        ]}
        total={data?.total}
        error={!!error}
      />
      {accessesDialog}

      {showNewItemDialog && (
        <ModalDialog onClose={() => setShowNewItemDialog(false)} title={i18n.translation.systems.new} fitContent>
          <SystemForm />
        </ModalDialog>
      )}
      {!!itemToDelete && (
        <ModalDialog
          onClose={() => setItemToDelete(undefined)}
          title={i18n.translation.systems.delete}
          isSubmitting={isSubmitting}
          actions={[
            {
              title: i18n.translation.common.cancel,
              onClick: () => setItemToDelete(undefined),
              type: "secondary",
            },
            {
              title: i18n.translation.common.yes,
              onClick: () => {
                setIsSubmitting(true);
                systemsApi
                  .deleteAdminBorderSystem(itemToDelete.id)
                  .then(() => setItemToDelete(undefined))
                  .catch((err) => dispatch(showErrorNotification(i18n, err)))
                  .finally(() => setIsSubmitting(false));
              },
            },
          ]}
        >
          {i18n.translation.systems.questions.delete}
        </ModalDialog>
      )}
    </FlexContent>
  );
};

export default BorderSystems;
